import type React from 'react';
import { useCallback, useMemo } from 'react';

import { LeftFilterCheckCell } from '../components/LeftFilterCheckCell';
import { LeftFilterCheckHeader } from '../components/LeftFilterCheckHeader';
import { LeftFilterTitleCell } from '../components/LeftFilterTitleCell';
import type { ITableLikeTreeFilterItem } from '../context';

interface IUseTableTreeLeftFilterColumns {
    checkedIds: number[];
    items: ITableLikeTreeFilterItem[];
    onCheckChange: (checkedIds: number[], item?: ITableLikeTreeFilterItem, isChecked?: boolean) => any;
    disabled?: boolean;
    title?: string;
    isMenuColumnVisible?: boolean;
    selectedId?: number;
    mainCheckedIds: number[];
    showCheckedMainItems?: boolean;
    checkedCountCache?: Record<string, number>;
}

export const useTableTreeLeftFilterColumns = (props: IUseTableTreeLeftFilterColumns) => {
    const {
        checkedIds,
        items,
        onCheckChange,
        disabled,
        title,
        isMenuColumnVisible = true,
        selectedId,
        mainCheckedIds,
        showCheckedMainItems,
        checkedCountCache,
    } = props;

    const onCheckAllChange = useCallback(
        (checked: boolean) => {
            onCheckChange(checked ? items.map((i) => i.id) : []);
        },
        [items, onCheckChange],
    );

    const onCheckOneChange = useCallback(
        (item: ITableLikeTreeFilterItem, checked: boolean) => {
            onCheckChange(checked ? [...checkedIds, item.id] : checkedIds.filter((c) => c !== item.id), item, checked);
        },
        [checkedIds, onCheckChange],
    );

    return useMemo(
        () => [
            ...(isMenuColumnVisible
                ? [
                      {
                          title: (
                              <LeftFilterCheckHeader
                                  checkedIds={checkedIds}
                                  items={items}
                                  onCheckChange={onCheckAllChange}
                                  disabled={disabled}
                              />
                          ),
                          accessor: 'menu-column',
                          align: 'center',
                          width: 52,
                          renderCell: (item: any) => (
                              <LeftFilterCheckCell
                                  onCheckChange={onCheckOneChange}
                                  checkedIds={checkedIds}
                                  item={item}
                                  disabled={disabled}
                              />
                          ),
                      },
                  ]
                : []),
            {
                title: title || 'Настройка структуры',
                accessor: 'title',
                align: 'left',
                renderCell: (item: any) => (
                    <LeftFilterTitleCell
                        item={item}
                        checkedIds={mainCheckedIds}
                        selectedId={selectedId}
                        showCheckedMainItems={showCheckedMainItems}
                        checkedCountCache={checkedCountCache}
                    />
                ),
            },
        ],
        [
            checkedCountCache,
            checkedIds,
            disabled,
            isMenuColumnVisible,
            items,
            mainCheckedIds,
            onCheckAllChange,
            onCheckOneChange,
            selectedId,
            showCheckedMainItems,
            title,
        ],
    );
};
